import React, { useEffect, useCallback, useLayoutEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  DataGridPro as MuiDataGrid,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import DataGridToolbar from './DataGridToolbar/DataGridToolbar';

import * as styles from './DataGrid.scss';

// Options for auto-sizing column widths
const defaultAutoSizeOptions = {
  includeHeaders: true,
  includeOutliers: true,
  expand: true,
};

const DataGrid = (props) => {
  const apiRef = useGridApiRef();

  const {
    id,
    autoSizeOptions,
    checkboxSelection,
    columns,
    columnVisibilityModel,
    defaultInitialState,
    disableRowSelectionOnClick,
    enableColumnsButton,
    enableDensityButton,
    enableExportButton,
    enableFilterButton,
    enableRefreshButton,
    filterModel,
    isLoading,
    keepNonExistentRowsSelected,
    localeText,
    noRowsOverlay,
    onColumnVisibilityModelChange,
    onFilterModelChange,
    onRowClick,
    refreshFn,
    rows,
    rowSelectionModel,
    onRowSelectionModelChange,
    showQuickFilter,
    slotProps,
    toolbarActionButtons,
  } = props;

  const [initialState, setInitialState] = useState();

  // Takes a snapshot of the DataGrid state, including filters, sorting, column visibility, etc.
  const snapshotState = React.useCallback(() => {
    if (id && apiRef?.current?.exportState && sessionStorage) {
      const currentState = apiRef.current.exportState();
      sessionStorage.setItem(`Bridg::${id}::dataGridState`, JSON.stringify(currentState));
    }
  }, [apiRef, id]);

  // Before browser first paints screen, check if we have a saved version of
  // the DataGrid state (filters, sorting, etc). Use that if found or fallback
  // to defaultInitialState prop.
  // Also, before window is unloaded, take a snapshot of the DataGrid state and
  // save it to session storage.
  useLayoutEffect(() => {
    const stateFromStorage = id ? sessionStorage?.getItem(`Bridg::${id}::dataGridState`) : false;
    setInitialState(stateFromStorage ? JSON.parse(stateFromStorage) : defaultInitialState ?? {});

    // Handles refreshing or navigating away
    window.addEventListener('beforeunload', snapshotState);

    return () => {
      window.removeEventListener('beforeunload', snapshotState);
      snapshotState();
    };
  }, [defaultInitialState, id, snapshotState]);

  // Auto-sizes columns to fit available width
  const autoSizeColumns = useCallback(() => {
    const autoSizeFn = apiRef.current?.autosizeColumns;
    if (autoSizeFn) autoSizeFn(autoSizeOptions ?? defaultAutoSizeOptions);
  }, [apiRef, autoSizeOptions]);

  // Resize columns anytime controlled column visibility model changes
  useEffect(() => {
    if (columnVisibilityModel) autoSizeColumns();
  }, [autoSizeColumns, columnVisibilityModel]);

  // Updates column visibility model and then auto-size columns
  const updateColumnVisibilityModel = useCallback((newModel) => {
    if (onColumnVisibilityModelChange) onColumnVisibilityModelChange(newModel);
    autoSizeColumns();
  }, [autoSizeColumns, onColumnVisibilityModelChange]);

  useEffect(() => {
    // Auto-sizes column widths based on available space -- dumb workaround for
    // something that Data Grid should do automatically.
    setTimeout(autoSizeColumns, 1);
  }, [autoSizeColumns, rows]);

  if (!initialState || !columns?.length) {
    return <CircularProgress />;
  }

  return (
    <div className={styles.brDataGrid}>
      <MuiDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        checkboxSelection={checkboxSelection}
        onRowClick={onRowClick}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        keepNonExistentRowsSelected={keepNonExistentRowsSelected}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        loading={isLoading}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={updateColumnVisibilityModel}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        onPaginationModelChange={autoSizeColumns}
        onResize={autoSizeColumns}
        resizeThrottleMs={333}
        ignoreDiacritics
        pagination
        autoPageSize
        hideFooterSelectedRowCount
        initialState={initialState}
        slots={{
          toolbar: DataGridToolbar,
          noRowsOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
          toolbar: {
            showQuickFilter,
            quickFilterProps: {
              variant: 'outlined',
              debounceMs: 300,
            },
            enableFilterButton,
            enableColumnsButton,
            enableDensityButton,
            enableExportButton,
            enableRefreshButton,
            refreshFn,
            isRefreshing: isLoading,
            actionButtons: toolbarActionButtons,
          },
          pagination: {
            showFirstButton: true,
            showLastButton: true,
          },
          ...slotProps,
        }}
        localeText={{
          toolbarColumns: '',
          toolbarDensity: '',
          toolbarExport: '',
          toolbarFilters: '',
          toolbarQuickFilterPlaceholder: 'Search...',
          ...localeText,
        }}
        sx={{
          // Don't add border around last clicked table cell
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          // Don't add border around last clicked header cell
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          borderColor: 'rgb(213, 229, 252)',
          '--DataGrid-rowBorderColor': 'rgb(213, 229, 252)',
        }}
      />
    </div>
  );
};

export default DataGrid;
